import type { JSX } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ExperienceFormFields } from '@/features/experiences/components/ExperienceFormFields';
import {
  buildRequest,
  getFormModeConfig,
} from '@/features/experiences/components/ExperienceFormDialog.utils';
import { useCandidateExperiences } from '../hooks/useCandidateExperiences';
import { useCandidateExperienceForm } from '../hooks/useCandidateExperienceForm';
import type { Experience } from '@/features/experiences/types/experience.types';

interface CandidateExperienceFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  experience?: Experience | null;
}

export function CandidateExperienceFormDialog({
  open,
  onOpenChange,
  mode,
  experience,
}: CandidateExperienceFormDialogProps): JSX.Element {
  const { createExperience, updateExperience } = useCandidateExperiences();
  const { form, flags } = useCandidateExperienceForm(mode, experience);
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = form;

  const handleClose = (): void => onOpenChange(false);

  const onSubmit = handleSubmit((data) => {
    const request = buildRequest(data);
    if (mode === 'create') createExperience(request);
    else if (experience) updateExperience(experience.id, request);
    handleClose();
  });

  const { title, submitLabel } = getFormModeConfig(mode);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <ExperienceFormFields
            register={register}
            errors={errors}
            setValue={setValue}
            {...flags}
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">{submitLabel}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
